import { RefObject, useCallback, useLayoutEffect, useState } from 'react';

const px = (value: string): number => Number.parseFloat(value) || 0;

const isScrollable = (element: Element): boolean =>
  /(auto|scroll)/.test(getComputedStyle(element).overflowY);

/** Nearest ancestor that scrolls, or null when the window does. */
export const findScrollParent = (element: HTMLElement): HTMLElement | null => {
  let node = element.parentElement;
  while (node && node !== document.body) {
    if (isScrollable(node)) return node;
    node = node.parentElement;
  }
  return null;
};

/** Height of the siblings after `node` that are laid out below it. */
const siblingsBelowHeight = (node: Element, gap: number): number => {
  const nodeBottom = node.getBoundingClientRect().bottom;
  let total = 0;
  let sibling = node.nextElementSibling;
  while (sibling) {
    const rect = sibling.getBoundingClientRect();
    if (rect.top >= nodeBottom - 1) {
      const style = getComputedStyle(sibling);
      total += rect.height + px(style.marginTop) + px(style.marginBottom) + gap;
    }
    sibling = sibling.nextElementSibling;
  }
  return total;
};

/**
 * Height laid out below `element` inside the scroll area: the following
 * siblings at every level, plus the bottom margin, padding and border of every
 * box between the element and the scroll area. The element's own height is
 * not part of it, so the value stays the same while that height changes.
 * Siblings beside the element, such as a sidebar in a flex row, do not count.
 */
const heightBelow = (
  element: HTMLElement,
  scrollParent: HTMLElement | null,
): number => {
  const stopAt = scrollParent ?? document.documentElement;
  let below = 0;
  let node: HTMLElement = element;
  while (node !== stopAt && node.parentElement) {
    const parent = node.parentElement;
    const parentStyle = getComputedStyle(parent);
    below +=
      px(getComputedStyle(node).marginBottom) +
      siblingsBelowHeight(node, px(parentStyle.rowGap)) +
      px(parentStyle.paddingBottom);
    if (parent !== stopAt) below += px(parentStyle.borderBottomWidth);
    node = parent;
  }
  return below;
};

/**
 * Space from the top of `element` to the bottom of the visible scroll area,
 * minus what sits below the element in that area, never less than
 * `minHeight`. A scroll area taller than the window grows with its content
 * and the window scrolls instead, so the window bottom is the limit then.
 * Positions are taken as if nothing were scrolled.
 */
export const measureAvailableHeight = (
  element: HTMLElement,
  minHeight: number,
): number => {
  const scrollParent = findScrollParent(element);
  const top =
    element.getBoundingClientRect().top +
    window.scrollY +
    (scrollParent?.scrollTop ?? 0);
  const parentBottom = scrollParent
    ? scrollParent.getBoundingClientRect().top +
      window.scrollY +
      scrollParent.clientTop +
      scrollParent.clientHeight
    : Number.POSITIVE_INFINITY;
  const areaBottom = Math.min(window.innerHeight, parentBottom);
  const available = areaBottom - top - heightBelow(element, scrollParent);
  return Math.max(minHeight, Math.floor(available));
};

/**
 * Height in pixels that the element referenced by `ref` can take before the
 * content below it leaves the scroll area. It is measured again after every
 * render, on window resize and zoom, and when the scroll area resizes. The
 * value is undefined before the first layout and while `enabled` is false.
 */
export const useFillHeight = (
  ref: RefObject<HTMLElement | null>,
  minHeight: number,
  enabled = true,
): number | undefined => {
  const [height, setHeight] = useState<number>();

  const measure = useCallback(() => {
    const element = ref.current;
    if (!enabled || !element) return;
    const next = measureAvailableHeight(element, minHeight);
    setHeight((previous) => (previous === next ? previous : next));
  }, [enabled, minHeight, ref]);

  useLayoutEffect(measure);

  useLayoutEffect(() => {
    const element = ref.current;
    if (!enabled || !element) return undefined;
    const observer =
      'ResizeObserver' in window ? new ResizeObserver(measure) : null;
    observer?.observe(findScrollParent(element) ?? document.body);
    window.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [enabled, measure, ref]);

  return enabled ? height : undefined;
};
